import { apiClient } from '../client';
import type { components } from '../schema.gen';

export type BotRead = components['schemas']['BotRead'];
export type BotStatusResponse = components['schemas']['BotStatusResponse'];
export type BotListResponse = components['schemas']['BotListResponse'];
export type BotCreate = components['schemas']['BotCreate'];
export type BotUpdate = components['schemas']['BotUpdate'];
export type BotLifecycleResponse = components['schemas']['BotLifecycleResponse'];
export type BotHaltResponse = components['schemas']['BotHaltResponse'];
export type BotResumeResponse = components['schemas']['BotResumeResponse'];
export type CancelAllResponse = components['schemas']['CancelAllResponse'];
export type SignalLogListResponse = components['schemas']['SignalLogListResponse'];
export type RiskLogListResponse = components['schemas']['RiskLogListResponse'];
export type EventStoreListResponse = components['schemas']['EventStoreListResponse'];
export type BotResolvedConfigResponse = components['schemas']['BotResolvedConfigResponse'];
export type ConfigPreviewRequest = components['schemas']['ConfigPreviewRequest'];
export type ConfigPreviewResponse = components['schemas']['ConfigPreviewResponse'];

export interface BotListParams {
  limit?: number;
  offset?: number;
  status?: string;
  [key: string]: unknown;
}

export interface BotSublistParams {
  limit?: number;
  offset?: number;
  [key: string]: unknown;
}

export async function getBots(params: BotListParams = {}): Promise<BotListResponse> {
  const { data } = await apiClient.get<BotListResponse>('/api/v1/bots', { params });
  return data;
}

export async function getBot(id: string | number): Promise<BotRead> {
  const { data } = await apiClient.get<BotRead>(`/api/v1/bots/${id}`);
  return data;
}

export async function createBot(body: BotCreate): Promise<BotRead> {
  const { data } = await apiClient.post<BotRead>('/api/v1/bots', body);
  return data;
}

export async function updateBot(id: string | number, body: BotUpdate): Promise<BotRead> {
  const { data } = await apiClient.patch<BotRead>(`/api/v1/bots/${id}`, body);
  return data;
}

export async function deleteBot(id: string | number): Promise<void> {
  await apiClient.delete(`/api/v1/bots/${id}`);
}

export async function getBotStatus(id: string | number): Promise<BotStatusResponse> {
  const { data } = await apiClient.get<BotStatusResponse>(`/api/v1/bots/${id}/status`);
  return data;
}

export async function startBot(id: string | number): Promise<BotLifecycleResponse> {
  const { data } = await apiClient.post<BotLifecycleResponse>(`/api/v1/bots/${id}/start`);
  return data;
}

export async function stopBot(id: string | number): Promise<BotLifecycleResponse> {
  const { data } = await apiClient.post<BotLifecycleResponse>(`/api/v1/bots/${id}/stop`);
  return data;
}

export async function restartBot(id: string | number): Promise<BotLifecycleResponse> {
  const { data } = await apiClient.post<BotLifecycleResponse>(`/api/v1/bots/${id}/restart`);
  return data;
}

export async function haltBot(id: string | number, reason: string): Promise<BotHaltResponse> {
  const { data } = await apiClient.post<BotHaltResponse>(`/api/v1/bots/${id}/halt`, { reason });
  return data;
}

export async function cancelAllOrders(id: string | number): Promise<CancelAllResponse> {
  const { data } = await apiClient.post<CancelAllResponse>(`/api/v1/bots/${id}/cancel-all`);
  return data;
}

export async function resumeBot(
  id: string | number,
  confirmation_token: string,
): Promise<BotResumeResponse> {
  const { data } = await apiClient.post<BotResumeResponse>(`/api/v1/bots/${id}/resume`, {
    confirmation_token,
  });
  return data;
}

export async function getBotSignals(
  id: string | number,
  params: BotSublistParams = {},
): Promise<SignalLogListResponse> {
  const { data } = await apiClient.get<SignalLogListResponse>(`/api/v1/bots/${id}/signals`, {
    params,
  });
  return data;
}

export async function getBotRiskLog(
  id: string | number,
  params: BotSublistParams = {},
): Promise<RiskLogListResponse> {
  const { data } = await apiClient.get<RiskLogListResponse>(`/api/v1/bots/${id}/risk-log`, {
    params,
  });
  return data;
}

export async function getBotEvents(
  id: string | number,
  params: BotSublistParams = {},
): Promise<EventStoreListResponse> {
  const { data } = await apiClient.get<EventStoreListResponse>(`/api/v1/bots/${id}/events`, {
    params,
  });
  return data;
}

export async function getBotResolvedConfig(
  id: string | number,
): Promise<BotResolvedConfigResponse> {
  const { data } = await apiClient.get<BotResolvedConfigResponse>(`/api/v1/bots/${id}/config`);
  return data;
}

export async function previewBotConfig(
  id: string | number,
  body: ConfigPreviewRequest,
): Promise<ConfigPreviewResponse> {
  const { data } = await apiClient.post<ConfigPreviewResponse>(
    `/api/v1/bots/${id}/config/preview`,
    body,
  );
  return data;
}

export async function getBotConfigHistory(
  id: string | number,
  params: BotSublistParams = {},
): Promise<EventStoreListResponse> {
  const { data } = await apiClient.get<EventStoreListResponse>(
    `/api/v1/bots/${id}/config/history`,
    { params },
  );
  return data;
}

export type BotAnalyticsRead = components['schemas']['BotAnalyticsRead'];
export type ExecutionQualityRead = components['schemas']['ExecutionQualityRead'];
export type BotSummaryRead = components['schemas']['BotSummaryRead'];
export type DriftReportRead = components['schemas']['DriftReportRead'];
export type AttributionReport = components['schemas']['AttributionReport'];
export type RollbackRequest = components['schemas']['RollbackRequest'];
export type RollbackResponse = components['schemas']['RollbackResponse'];

export type AnalyticsWindow = '7d' | '30d' | '90d' | 'all';

export async function getBotAnalytics(
  id: string | number,
  window: AnalyticsWindow = '30d',
): Promise<BotAnalyticsRead> {
  const { data } = await apiClient.get<BotAnalyticsRead>(`/api/v1/bots/${id}/analytics`, {
    params: { window },
  });
  return data;
}

export async function getBotExecutionQuality(
  id: string | number,
  window = 30,
): Promise<ExecutionQualityRead> {
  const { data } = await apiClient.get<ExecutionQualityRead>(
    `/api/v1/bots/${id}/execution-quality`,
    {
      params: { window },
    },
  );
  return data;
}

export async function getBotSummary(id: string | number): Promise<BotSummaryRead> {
  const { data } = await apiClient.get<BotSummaryRead>(`/api/v1/bots/${id}/summary`);
  return data;
}

/** GET /bots/{id}/drift — compare live signals against backtest for drift detection */
export async function getBotDrift(
  id: string | number,
  window: AnalyticsWindow = '30d',
): Promise<DriftReportRead> {
  const { data } = await apiClient.get<DriftReportRead>(`/api/v1/bots/${id}/drift`, {
    params: { window },
  });
  return data;
}

/** GET /bots/{id}/attribution — PnL attribution by strategy */
export async function getBotAttribution(
  id: string | number,
  window: AnalyticsWindow = '30d',
  bucket?: 'daily' | 'monthly' | 'none',
): Promise<AttributionReport> {
  const { data } = await apiClient.get<AttributionReport>(`/api/v1/bots/${id}/attribution`, {
    params: { window, ...(bucket && { bucket }) },
  });
  return data;
}

/** POST /bots/{id}/config/rollback — rollback bot config to a previous version */
export async function rollbackBotConfig(
  id: string | number,
  body: RollbackRequest,
): Promise<RollbackResponse> {
  const { data } = await apiClient.post<RollbackResponse>(
    `/api/v1/bots/${id}/config/rollback`,
    body,
  );
  return data;
}

// Normalise runtime_state string → frontend BotStatus constant.
// IMPORTANT: Only pass BotStatusResponse.runtime_state or BotHealthSummary.runtime_state here.
// Do NOT pass BotRead.status ("ACTIVE"/"DISABLED") — that is a record status, not runtime state.
export function normaliseBotStatus(
  runtimeState: string,
): import('@/shared/lib/constants').BotStatus {
  const map: Record<string, import('@/shared/lib/constants').BotStatus> = {
    running: 'RUNNING',
    stopped: 'STOPPED',
    stopping: 'STOPPED',
    stop_timeout: 'STOPPED',
    halted: 'HALTED',
    crashed: 'CRASHED',
    error: 'CRASHED',
    starting: 'STARTING',
    initializing: 'STARTING',
  };
  return map[runtimeState.toLowerCase()] ?? 'STOPPED';
}
